import { Avatar, Button, Col, Form, Input, Row } from 'antd'
import UploadSingleImage from 'components/global/UploadSingleImage'
import UploadDocument from 'components/global/UploadDocument'
import GlobalCounterStep from 'components/global/CounterStep'
import CustomModal from 'components/global/CustomModal'
import { useCreateNewTeamForLeague } from 'api/Team/LeagueTeam'
import { leagueEventStore } from 'state/league/leagueEvent'
import { manageTeamStore } from 'state/league/manageTeam'
import { myStore } from 'state/store'
import { LeagueFinalResp } from 'types/league/league'
import { Photo } from 'types/globalTypes'
import React, { useState } from 'react'
import DivisionSelect from './ModalContent/Inputs/DivisionSelect'

const steps = [{ title: 'Team Details' }, { title: 'League Confirmation' }]

const CreateLeagueTeamModal: React.FC = () => {
  const [form] = Form.useForm()
  const [step, setStep] = useState(0)
  const [step1Data, setStep1Data] = useState<{ teamName: string; teamAbout?: string }>({ teamName: '' })
  const [profilePhotoId, setProfilePhotoId] = useState<string>('')
  const [profilePhoto, setProfilePhoto] = useState<Photo>(undefined)
  const [coverPhotoIds, setCoverPhotoIds] = useState<string[]>([])

  const {
    createLeagueTeamModal,
    setCreateLeagueTeamModal,
    setManageTeamModal,
    setStepCounter,
  } = manageTeamStore(store => ({
    createLeagueTeamModal: store.createLeagueTeamModal,
    setCreateLeagueTeamModal: store.setCreateLeagueTeamModal,
    setManageTeamModal: store.setManageTeamModal,
    setStepCounter: store.setStepCounter,
  }))

  const { leagueData }: { leagueData: LeagueFinalResp } = leagueEventStore(
    store => ({ leagueData: store.leagueData }),
  )

  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)
  const { mutate: createNewTeamForLeague } = useCreateNewTeamForLeague()

  const resetAndClose = () => {
    form.resetFields()
    setStep(0)
    setStep1Data({ teamName: '' })
    setProfilePhotoId('')
    setProfilePhoto(undefined)
    setCoverPhotoIds([])
    setStepCounter(0)
    setCreateLeagueTeamModal(false)
    setManageTeamModal(true)
  }

  const handleNext = async () => {
    try {
      await form.validateFields(['teamName'])
      setStep1Data({
        teamName: form.getFieldValue('teamName'),
        teamAbout: form.getFieldValue('teamAbout'),
      })
      setStep(1)
    } catch {}
  }

  const handleFinish = (values: any) => {
    const payload = {
      leagueId: leagueData._id,
      divisionId: values.divisionId,
      price: Number(values.price),
      minPlayers: Number(values.minPlayers),
      maxPlayers: Number(values.maxPlayers),
      team: {
        name: step1Data.teamName,
        about: step1Data.teamAbout || undefined,
        profilePhotoId: profilePhotoId || undefined,
        coverPhotoId: coverPhotoIds[0] || undefined,
      },
    }
    setModalBtnLoader(true)
    createNewTeamForLeague(
      { organizationId: leagueData.organizationId, payload },
      {
        onSuccess: () => {
          setModalBtnLoader(false)
          resetAndClose()
        },
        onError: () => {
          setModalBtnLoader(false)
        },
      },
    )
  }

  const footer =
    step === 0 ? (
      <div className="flex justify-end pb-24px pr-30px pt-16px">
        <Button
          type="primary"
          onClick={handleNext}
          className="br-8 h-44px px-24px"
        >
          Next
        </Button>
      </div>
    ) : (
      <div className="flex justify-end gap-16px pb-24px pr-30px pt-16px">
        <Button onClick={() => setStep(0)} className="br-8 h-44px px-24px">
          Back
        </Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          className="br-8 h-44px px-24px"
        >
          Save
        </Button>
      </div>
    )

  return (
    <CustomModal
      openModal={createLeagueTeamModal}
      isCloseModalCustom
      closeModal={resetAndClose}
      title="Add team"
      width={580}
      footer={footer}
    >
      <>
        <GlobalCounterStep steps={steps} currentStep={step} />
        <Form form={form} layout="horizontal" onFinish={handleFinish}>
          {step === 0 && (
            <div className="px-15px pt-16px pb-24px">
              <Row gutter={24} className="mb-16px">
                <Col span={24}>
                  <div className="flex align-center gap-24px">
                    <Avatar
                      size={80}
                      src={profilePhoto?.thumb || profilePhoto?.original}
                    />
                    <UploadSingleImage
                      fileId={profilePhotoId}
                      setFileId={setProfilePhotoId}
                      file={profilePhoto}
                      setFile={setProfilePhoto}
                      sourceType="other"
                    />
                  </div>
                </Col>
              </Row>

              <Row gutter={24} className="">
                <Col span={24}>
                  <label className="input-label required-input">
                    Team name
                  </label>
                  <Form.Item
                    colon={false}
                    name="teamName"
                    rules={[
                      { required: true, message: 'Team name is required' },
                    ]}
                  >
                    <Input
                      placeholder="Enter team name"
                      className="br-12"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24} className="">
                <Col span={24}>
                  <label className="input-label">About the team</label>
                  <Form.Item colon={false} name="teamAbout">
                    <Input.TextArea
                      placeholder="Type here details about the team"
                      className="br-12"
                      rows={4}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24} className="">
                <Col span={24}>
                  <label className="input-label">Cover image</label>
                  <UploadDocument
                    documentIds={coverPhotoIds}
                    setDocumentIds={setCoverPhotoIds}
                    multiple={false}
                    docType="image/*"
                    files={[]}
                  />
                </Col>
              </Row>
            </div>
          )}

          {step === 1 && (
            <div className="px-15px pt-16px pb-24px">
              <Row gutter={24} className="">
                <Col span={24}>
                  <label className="input-label required-input">
                    Select Division
                  </label>
                  <Form.Item
                    colon={false}
                    name="divisionId"
                    rules={[
                      { required: true, message: 'Division is required' },
                    ]}
                  >
                    <DivisionSelect
                      form={form}
                      data={undefined}
                      leagueId={leagueData._id}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24} className="">
                <Col span={12}>
                  <label className="input-label required-input">Cost</label>
                  <Form.Item
                    colon={false}
                    name="price"
                    rules={[{ required: true, message: 'Cost is required' }]}
                  >
                    <Input
                      type="number"
                      min={0}
                      placeholder="$ 0.00"
                      className="br-12"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={24} className="">
                <Col span={12}>
                  <label className="input-label required-input">
                    Min. Players
                  </label>
                  <Form.Item
                    colon={false}
                    name="minPlayers"
                    rules={[
                      { required: true, message: 'Min. players are required' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const max = getFieldValue('maxPlayers')
                          if (!value || !max) return Promise.resolve()
                          if (Number(value) < Number(max))
                            return Promise.resolve()
                          return Promise.reject(
                            new Error(
                              'Min. players must be less than Max. players',
                            ),
                          )
                        },
                      }),
                    ]}
                  >
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="br-12"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <label className="input-label required-input">
                    Max. Players
                  </label>
                  <Form.Item
                    colon={false}
                    name="maxPlayers"
                    rules={[
                      { required: true, message: 'Max. players are required' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const min = getFieldValue('minPlayers')
                          if (!value || !min) return Promise.resolve()
                          if (Number(value) > Number(min))
                            return Promise.resolve()
                          return Promise.reject(
                            new Error(
                              'Max. players must be greater than Min. players',
                            ),
                          )
                        },
                      }),
                    ]}
                  >
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="br-12"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </>
    </CustomModal>
  )
}

export default CreateLeagueTeamModal
